import { Observable, of, throwError, zip } from 'rxjs';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { catchError, map } from 'rxjs/operators';
import { LogLevel } from '../diagnostics/log-level';
import { LogRecord } from '../diagnostics/log-record';
import { Logging } from '../diagnostics/logging';
import { Http } from './http';
import { Language, LanguageInventory } from './language';

/**
 * Interface that contains all the possible localized resources in the json file
 */
interface LocalizedResources<T> {
    Strings: T;
}

/**
 * Options to initialize the localization manager
 */
export interface LocalizationManagerOptions {
    /**
     * The URL path (relative or absolute)
     * to the resources folder containing the json files with the localized assets.
     * defaults to '/assets/strings' if not specified.
     * Ex: '/assets/strings'
     */
    resourcesPath?: string;

    /**
     * The Azure locale to start localization manager with
     */
    azureLocale?: string;
}

export interface LocaleSet {
    /**
     * The locale ID covering all reginal locale.
     */
    id: string;

    /**
     * The neutral ID used for loading string resources.
     */
    neutral: string;
}

/**
 * Class to retrieve localized resources based on the user locale
 * This class lets you load resources from a json file in an
 * arbitrary location and determines what locale resources to return
 * on the user preference
 */
export class LocalizationManager<T> {
    private static defaultResourcesStringsFolder = 'assets/strings';
    private static resourcesStringsFile = '/strings.json';
    private static resourcesStringsFileFormat = '/{0}/strings.json';
    private static languageManager = new LanguageInventory();
    public static localStorageLocaleKey = 'locale:@msft-sme/shell';
    public static localStorageLocaleSetKey = 'localeSet:@msft-sme/shell';
    public static neutralCultures: Language[] = LocalizationManager.languageManager.neutralCultures;
    public static regionalCultures: Language[] = LocalizationManager.languageManager.regionalCultures;

    private readonly defaultLocaleId = LocalizationManager.neutralCultures[0].id;
    private resourcesStringFormat: string;
    private resourcesStringDefaultFile: string;

    private http: Http = new Http();
    private localeIdInternal: LocaleSet;
    private resourcesPath: string;

    /**
     * Initializes a new instance of the LocalizationManager class that reads the localized assets from
     * the given locations.
     * @param options? The options to initialize the localization manager.
     */
    public constructor(private options?: LocalizationManagerOptions) {
        const resourcesPath = options && options.resourcesPath;
        if (resourcesPath) {
            this.resourcesPath = MsftSme.trimEnd(resourcesPath.trim(), '/');
        } else {
            this.resourcesPath = LocalizationManager.defaultResourcesStringsFolder;
        }

        const azureLocale = options && options.azureLocale;
        if (azureLocale) {
            const azureLocaleSet = this.normalizeAzureLocaleId(azureLocale);
            if (azureLocaleSet) {
                this.localeIdInternal = azureLocaleSet;
            }
        }

        this.initializeResources();
    }

    /**
     * Gets current locale.
     * @return string the locale string.
     */
    public get localeId(): LocaleSet {
        return this.localeIdInternal;
    }

    /**
     * Gets the navigator language
     */
    public getNavigatorLanguage(): string {
        return navigator.language;
    }

    /**
     * Creates a LocaleSet from a string
     */
    public createLocaleSet(id: string, neutral?: string): LocaleSet {
        return { id: id, neutral: neutral || id };
    }

    /**
     * Sets the current locale in persistent storage
     * @param localeId the string representing the locale selected by the user. Ex: 'es' or 'en'
     */
    public saveLocale(localeSet: LocaleSet): void {
        if (!this.checkBothAvailable(localeSet)) {
            throw new Error(`The locale specified, ${localeSet.id} and ${localeSet.neutral} were not recognized.`);
        }

        this.localeIdInternal = localeSet;

        MsftSme.LocalStorageHandler.setItem(LocalizationManager.localStorageLocaleSetKey, JSON.stringify(localeSet));

        MsftSme.LocalStorageHandler.setItem(LocalizationManager.localStorageLocaleKey, localeSet.neutral);
        this.updateDocumentLanguage();
    }

    /**
     * Updates the lang attribute of the document to reflect the current locale.
     */
    public updateDocumentLanguage(): any {
        const locale = this.getLocaleId();
        document.documentElement.setAttribute('lang', locale.id);
    }

    /**
     * Ensures Resources are Initialized
     */
    private initializeResources() {
        const self = MsftSme.self();
        const locale = this.getLocaleId();
        self.Resources = <MsftSme.MsftSmeResources>{
            strings: {},
            localeId: locale.neutral,
            localeRegionalId: locale.id
        };
    }

    /**
     * Gets the current locale.
     * Throughout code, locale code is using <lower case>-<upper case> format which is standard on both Microsoft Edge and Google Chrome.
     * ex) en-US, de-DE, ja-JP and so on.
     * @returns The current locale selected by the user
     */
    public getLocaleId(): LocaleSet {
        if (this.localeIdInternal) {
            return this.localeIdInternal;
        }

        const storageLocaleSet = MsftSme.LocalStorageHandler.getItem(LocalizationManager.localStorageLocaleSetKey);
        let localeSet: LocaleSet;
        if (storageLocaleSet) {
            localeSet = JSON.parse(storageLocaleSet);
            if (this.checkBothAvailable(localeSet)) {
                return this.localeIdInternal = localeSet;
            }
        }

        // Now we read the browser locales and if it's supported, then we use that otherwise use default locale
        const navigatorLanguage = this.getNavigatorLanguage();
        if (!navigatorLanguage) {
            return this.localeIdInternal = { id: this.defaultLocaleId, neutral: this.defaultLocaleId };
        }

        localeSet = { id: navigatorLanguage, neutral: navigatorLanguage };
        if (this.checkBothAvailable(localeSet)) {
            return this.localeIdInternal = localeSet;
        }

        // Try with the neutral part only
        const neutral = navigatorLanguage.split('-')[0].toLowerCase();
        const found = LocalizationManager.neutralCultures.find(item => item.id.split('-')[0] === neutral);
        if (found) {
            // adjust default string language.
            localeSet.neutral = found.id;
            if (this.checkIdAvailable(localeSet)) {
                return this.localeIdInternal = localeSet;
            }

            // cannot found the regional code.
            return this.localeIdInternal = { id: found.id, neutral: found.id };
        }

        return this.localeIdInternal = { id: this.defaultLocaleId, neutral: this.defaultLocaleId };
    }

    /**
     * Fetches the localized strings from the server based on the current culture.
     * @returns an observable with object the localized strings.
     */
    public fetchLocalizedStrings(): Observable<T> {
        this.configureFetchFiles();
        this.getLocaleId();
        if (this.localeId.neutral === this.defaultLocaleId) {
            // Only fetch the default locale
            return this.fetchDefaultStrings();
        }

        return zip(
            this.fetchDefaultStrings(),
            this.fetchLocaleStrings())
            .pipe(
                catchError(() => of(null)),
                map(([fetchDefault, fetchLocale]) => {
                    // get the english strings and replace those properties of the localized json
                    if (fetchLocale) {
                        return MsftSme.deepAssign({}, fetchDefault, fetchLocale);
                    }

                    return fetchDefault;
                }));
    }

    private configureFetchFiles(): void {
        const version = MsftSme.self() && MsftSme.self().Environment && MsftSme.self().Environment.version || 'no-version';
        this.resourcesStringFormat = `${this.resourcesPath}${LocalizationManager.resourcesStringsFileFormat}?v=${version}`;
        this.resourcesStringDefaultFile = `${this.resourcesPath}${LocalizationManager.resourcesStringsFile}?v=${version}`;
    }

    public checkBothAvailable(localeSet: LocaleSet): boolean {
        if (!localeSet || !localeSet.neutral || !localeSet.id) {
            return false;
        }

        const checkNeutral = LocalizationManager.neutralCultures.find(item => item.id === localeSet.neutral);
        const checkId = LocalizationManager.regionalCultures.find(item => item.id === localeSet.id);
        return !!checkNeutral && !!checkId;
    }

    private checkIdAvailable(localeSet: LocaleSet): boolean {
        const checkId = LocalizationManager.regionalCultures.find(item => item.id === localeSet.id);
        return !!checkId;
    }

    private fetchDefaultStrings(): Observable<T> {
        return this.http.get(this.resourcesStringDefaultFile)
            .pipe(
                catchError((error: AjaxError) => {
                    if (error.status >= 400) {
                        // If we got any error, we just reply with that error and the map function will handle it
                        Logging.log(<LogRecord>{
                            source: 'LocalizationManager',
                            level: LogLevel.Error,
                            message:
                                `Error ${error.status} received when getting default localized strings for the ` +
                                `${this.defaultLocaleId} locale. The error was: ${error.message}`
                        });
                    }

                    return throwError(() => error);
                }),
                map((result: AjaxResponse<any>) => {
                    return (<LocalizedResources<T>>result.response).Strings;
                }));
    }

    private fetchLocaleStrings(): Observable<T> {
        return this.http.get(this.resourcesStringFormat.format(this.localeId.neutral))
            .pipe(
                catchError((error: AjaxError) => {
                    if (error.status >= 400) {
                        // If we got any error, we just reply with that error and the map function will handle it
                        Logging.log(<LogRecord>{
                            source: 'LocalizationManager',
                            level: LogLevel.Warning,
                            message:
                                `Error ${error.status} received when getting localized strings for the ` +
                                `user specified locale: '${this.localeId.neutral}'. The error was: ${error.message}`
                        });
                        return of({});
                    }
                }),
                map((result: AjaxResponse<any>) => {
                    // If response has body, use that
                    if (!result || !result.response) {
                        return null;
                    }

                    return (<LocalizedResources<T>>result.response).Strings;
                }));
    }

    private normalizeAzureLocaleId(azureLocale: string): LocaleSet {
        if (MsftSme.isNullOrWhiteSpace(azureLocale)) {
            return null;
        }

        const neutralCultures: { [index: string]: string } = {};
        neutralCultures['de'] = 'de-DE';
        neutralCultures['en'] = 'en-US';
        neutralCultures['es'] = 'es-ES';
        neutralCultures['fr'] = 'fr-FR';
        neutralCultures['it'] = 'it-IT';
        neutralCultures['hu'] = 'hu-HU';
        neutralCultures['nl'] = 'nl-NL';
        neutralCultures['pl'] = 'pl-PL';
        neutralCultures['pt'] = 'pt-BR';
        neutralCultures['sv'] = 'sv-SE';
        neutralCultures['tr'] = 'tr-TR';
        neutralCultures['cs'] = 'cs-CZ';
        neutralCultures['ru'] = 'ru-RU';
        neutralCultures['zh'] = 'zh-CN';
        neutralCultures['zh-hans'] = 'zh-CN';
        neutralCultures['zh-chs'] = 'zh-CN';
        neutralCultures['zh-hant'] = 'zh-TW';
        neutralCultures['zh-cht'] = 'zh-TW';
        neutralCultures['ja'] = 'ja-JP';
        neutralCultures['ko'] = 'ko-KR';

        const languages = azureLocale.split('.');
        const segments = languages[1].split('-');
        const id = `${segments[0]}-${segments[1].toUpperCase()}`;
        const neutral = neutralCultures[languages[0]];
        if (!neutral) {
            return null;
        }

        const localeSet: LocaleSet = { id, neutral };
        if (this.checkBothAvailable(localeSet)) {
            return localeSet;
        }

        // cannot found the regional code.
        return <LocaleSet>{ id: localeSet.neutral, neutral: localeSet.neutral };
    }
}
